import { MouseEvent, ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, BoxProps } from 'reflexbox';
import styled, { css } from 'styled-components';

import { activateOnKeyDown, focusVisible } from '../../helpers';
import { tokens } from '../../theme/tokens';
import { Skeleton } from '../Skeleton';
import { Spinner } from '../Spinner';
import {
	AIButton,
	Destructive,
	DisabledAI,
	DisabledDestructive,
	DisabledGhost,
	DisabledPrimary,
	DisabledSecondary,
	Ghost,
	Primary,
	Secondary,
} from './ButtonTypes';

type ButtonTypes =
	| 'primary'
	| 'secondary'
	| 'tertiary'
	| 'ghost'
	| 'alternative'
	| 'destructive'
	| 'plain'
	| 'loading'
	| 'task-pending'
	| 'skeleton'
	| 'ai';

const ghostTypes = ['tertiary', 'plain', 'alternative', 'ghost'] as const;

const Container = styled(Box)<{
	type: ButtonTypes;
	$disabled?: boolean;
}>`
	border-radius: ${tokens.radius.full}px;
	padding: ${tokens.gap.inline}px;
	cursor: pointer;
	user-select: none;
	flex-shrink: 0;
	flex-grow: 0;
	box-sizing: border-box;
	transition: 0.1s ease-out all;
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;

	${focusVisible}

	svg {
		display: block;
		width: 16px;
		height: 16px;
		color: inherit;

		path {
			fill: currentColor;
		}
	}

	${(props) => props.type === 'primary' && !props.$disabled && Primary}
	${(props) => props.type === 'secondary' && !props.$disabled && Secondary}
	${(props) => props.type === 'ai' && !props.$disabled && AIButton}
	${(props) =>
		ghostTypes.includes(props.type as (typeof ghostTypes)[number]) &&
		!props.$disabled &&
		css`
			${Ghost}
			color: ${tokens.icon.primary};
		`}
	${(props) =>
		props.type === 'destructive' &&
		!props.$disabled &&
		css`
			${Destructive}
			background: ${tokens.surface.white};
			border-color: ${tokens.error.icon};
			color: ${tokens.error.icon};
			box-shadow: ${tokens.shadow.button};
		`}

	${(props) => props.$disabled && props.type === 'primary' && DisabledPrimary}
	${(props) => props.$disabled && props.type === 'secondary' && DisabledSecondary}
	${(props) => props.$disabled && props.type === 'ai' && DisabledAI}
	${(props) =>
		props.$disabled &&
		ghostTypes.includes(props.type as (typeof ghostTypes)[number]) &&
		DisabledGhost}
	${(props) => props.$disabled && props.type === 'destructive' && DisabledDestructive}

	${(props) =>
		props.$disabled &&
		css`
			pointer-events: none;
		`}

	${(props) =>
		props.type === 'loading' &&
		css`
			background: ${tokens.surface.primary};
			border: 1px solid transparent;
			padding: ${tokens.gap.element}px;
			color: ${tokens.icon.secondary};
			box-shadow: none;
		`}

	${(props) =>
		props.type === 'task-pending' &&
		css`
			background-color: transparent;
			border: none;
			outline: 2px dashed ${tokens.border.default};
			outline-offset: -2px;
			color: ${tokens.icon.secondary};
			box-shadow: none;
		`}

	${(props) =>
		props.type === 'skeleton' &&
		css`
			background: ${tokens.surface.skeleton};
			border: none;
			box-shadow: ${tokens.shadow.button};
			pointer-events: none;
			cursor: default;
		`}
`;

export function IconButton(
	props: BoxProps & {
		icon?: ReactNode;
		small?: boolean;
		link?: string;
		type?: ButtonTypes;
		title?: string;
		onClick?: (event: MouseEvent) => Promise<void> | void;
		className?: string;
		loading?: boolean;
		disabled?: boolean;
	},
) {
	const [loading, setLoading] = useState(false);

	const [pathname, params] = props.link?.split('?') ?? [];

	const { loading: loadingProp, disabled, onClick, icon, type, ...containerProps } = props;
	void loadingProp;

	const handleClick = async (event: MouseEvent) => {
		if (disabled) {
			return;
		}

		const result = onClick?.(event);
		if (result && typeof result.then === 'function') {
			setLoading(true);
			try {
				await result;
			} finally {
				setLoading(false);
			}
		}
	};

	if (loading || props.loading) {
		return (
			<Container {...containerProps} type="loading" $disabled={disabled}>
				<Spinner />
			</Container>
		);
	}

	if (type === 'skeleton') {
		return <IconButton.Skeleton />;
	}

	if (props.link && !disabled) {
		return (
			<Link to={{ pathname, search: params && `?${params}` }}>
				<Container
					{...containerProps}
					type={type ?? 'primary'}
					$disabled={disabled}
					onClick={handleClick}
				>
					{icon}
				</Container>
			</Link>
		);
	}

	const interactive = !!onClick && !disabled;

	return (
		<Container
			{...containerProps}
			type={type ?? 'primary'}
			$disabled={disabled}
			onClick={handleClick}
			role={interactive ? 'button' : undefined}
			tabIndex={interactive ? 0 : undefined}
			onKeyDown={
				interactive
					? (event) => activateOnKeyDown(event, () => event.currentTarget.click())
					: undefined
			}
		>
			{icon}
		</Container>
	);
}

IconButton.Skeleton = styled(Skeleton.Box)`
	width: 32px;
	height: 32px;
	flex-shrink: 0;
	border-radius: ${tokens.radius.full}px;
	box-shadow: ${tokens.shadow.button};
	animation: none;
	background: ${tokens.surface.skeleton};
`;
